/**
 * Bloques generados dentro del contenido de una sección de cotización.
 *
 * El contenido de `cotizaciones.secciones[].contenido` es HTML que se escribe a
 * mano. Para las secciones argumentales eso está bien, pero la de "Inversión"
 * repetía la tabla de ítems y el cronograma con los montos **tipeados como
 * texto**: cambiar el precio en el editor actualizaba `items`/`subtotal`/`total`
 * y dejaba la sección con el precio viejo, así que la página 1 y la página de
 * Inversión se contradecían delante del cliente (pasó con la 260031).
 *
 * Ahora la sección escribe un marcador y el importe sale de una sola fuente:
 *
 *   {{tabla_inversion}}    → tabla de ítems + subtotal/IGV/total
 *   {{cronograma_pagos}}   → cuotas de `cronograma_pagos`
 *   {{precio:390}}         → un importe suelto dentro de la prosa
 *
 * El tercero existe por el markup del partner. La tabla y el cronograma se
 * arman desde las columnas, así que un cambio de precio los arrastra solo;
 * pero un importe TIPEADO en medio de un párrafo —"la mensualidad es de
 * S/ 390"— no lo toca nada, y queda contradiciendo al resto del documento en
 * cuanto el partner aplica su markup. `{{precio:390}}` emite el importe **ya
 * multiplicado por `markup.factor`** y con su símbolo de moneda, así que el
 * autor escribe el marcador solo, sin el "S/" delante.
 *
 * Los títulos y la prosa alrededor siguen siendo del autor: el marcador emite
 * **solo la tabla**. Una sección sin marcador se renderiza tal cual, de modo
 * que las cotizaciones ya entregadas no cambian.
 *
 * MANTENER EN SINCRONÍA con `api/src/lib/cotizacion-bloques.ts` (esta copia la
 * usan el enlace público `/ver/:token` y la vista interna; el PDF usa la del
 * backend). Difieren solo en de dónde importan el formateo de moneda: cada
 * superficie formatea el dinero como el resto de su propio documento.
 */
import { formatCurrency as fmtMoneda, fmtPct } from './utils'

// ─── Datos que necesita un bloque ───

export interface CuotaCronograma {
  numero?: number;
  descripcion?: string;
  porcentaje?: number;
  monto?: number;
  condicion?: string;
}

export interface ItemInversion {
  nombre?: string;
  descripcion?: string;
  /**
   * Lo que el ítem le ahorra al cliente: «Recupera 22 horas al mes». Campo
   * propio y no parte de `descripcion` porque esto se escapa —y debe—, así
   * que una negrita escrita adentro saldría como `<strong>` literal.
   */
  ahorro?: string;
  cantidad?: number;
  precio_unitario?: number;
  total?: number;
}

export interface DatosBloques {
  items?: unknown;
  subtotal?: number;
  igv_porcentaje?: number;
  igv_monto?: number;
  descuento_porcentaje?: number;
  descuento_monto?: number;
  total?: number;
  moneda?: string;
  cronograma_pagos?: unknown;
  /** `false` = propuesta de alcance: el documento no lleva importes ni totales. */
  mostrar_precios?: boolean | null;
  branding?: { presupuesto_mas_igv?: boolean } | null;
  /**
   * El markup del partner. Sólo se usa para `{{precio:N}}`: los ítems y el
   * cronograma ya vienen con el precio aplicado desde el editor, y volver a
   * multiplicarlos acá lo cobraría dos veces.
   */
  markup?: { factor?: number } | null;
}

// ─── Utilidades ───

function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function num(value: unknown): number {
  const n = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : 0;
}

function r2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** El nombre del ítem; si no lo trae, la descripción hace de etiqueta. */
function etiquetaItem(item: ItemInversion): string {
  const nombre = (item.nombre ?? '').trim();
  const desc = (item.descripcion ?? '').trim();
  const ahorro = (item.ahorro ?? '').trim();
  // El ahorro cierra la celda en negrita y en su propia línea: es el dato que
  // el lector busca al recorrer la tabla, y mezclado en la prosa se perdía.
  const cola = ahorro ? '<br/><strong>' + esc(ahorro) + '</strong>' : '';
  if (nombre && desc) return esc(nombre) + ' — ' + esc(desc) + cola;
  return esc(nombre || desc) + cola;
}

function filaResumen(label: string, monto: string): string {
  return '<tr><td>' + esc(label) + '</td><td></td><td style="text-align:right">' + esc(monto) + '</td></tr>';
}

// ─── {{tabla_inversion}} ───

function tablaInversion(cot: DatosBloques): string {
  const moneda = cot.moneda || 'USD';
  const items: ItemInversion[] = Array.isArray(cot.items) ? (cot.items as ItemInversion[]) : [];
  const alcance = cot.mostrar_precios === false;

  // Propuesta de alcance: se ve QUÉ se hace, no cuánto cuesta. Sin importes la
  // tabla queda en una sola columna y no lleva totales — igual que la portada.
  if (alcance) {
    if (items.length === 0) return '';
    const filas = items.map(item => '<tr><td>' + etiquetaItem(item) + '</td></tr>').join('\n');
    return [
      '<table class="cot-section-table cot-section-table--prosa">',
      '<thead><tr><th>Qué incluye el servicio</th></tr></thead>',
      '<tbody>\n' + filas + '\n</tbody>',
      '</table>',
    ].join('\n');
  }

  // Sin ítems y sin importe no hay nada que imprimir: una tabla de ceros es
  // peor que no poner tabla.
  if (items.length === 0 && num(cot.total) === 0 && num(cot.subtotal) === 0) return '';

  const masIgv = !!cot.branding?.presupuesto_mas_igv;
  const descuento = num(cot.descuento_monto);

  const filasItems = items
    .map(item => {
      const cant = item.cantidad ?? 1;
      const total = item.total != null ? num(item.total) : r2(num(item.precio_unitario) * num(cant));
      return (
        '<tr><td>' + etiquetaItem(item) + '</td>' +
        '<td style="text-align:center">' + esc(cant) + '</td>' +
        '<td style="text-align:right">' + esc(fmtMoneda(total, moneda)) + '</td></tr>'
      );
    })
    .join('\n');

  // El subtotal es el importe que se cotiza cuando el documento va "+ IGV": la
  // etiqueta lo dice en vez de dejar al cliente adivinando qué incluye la cifra
  // del banner. Y una cotización con IGV en cero no lleva fila de impuesto: sin
  // impuesto el subtotal repetiría el total, así que solo se imprime cuando hay
  // un descuento que explicar.
  const sinIgv = num(cot.igv_porcentaje) <= 0 && num(cot.igv_monto) <= 0;

  const resumen: string[] = [];
  if (!sinIgv || descuento > 0) {
    resumen.push(
      filaResumen(masIgv ? 'Inversión (sin IGV)' : 'Subtotal', fmtMoneda(num(cot.subtotal), moneda)),
    );
  }
  if (descuento > 0) {
    const pct = num(cot.descuento_porcentaje);
    resumen.push(
      filaResumen(
        pct > 0 ? 'Descuento (' + fmtPct(pct) + '%)' : 'Descuento',
        '-' + fmtMoneda(descuento, moneda),
      ),
    );
  }
  if (!sinIgv) {
    resumen.push(
      filaResumen('IGV (' + (cot.igv_porcentaje ?? 18) + '%)', fmtMoneda(num(cot.igv_monto), moneda)),
    );
  }

  const etiquetaTotal = sinIgv
    ? 'Total general <span class="cot-sin-igv">sin IGV</span>'
    : masIgv
      ? 'Total con IGV'
      : 'Total';

  return [
    '<table class="cot-section-table cot-section-table--prosa">',
    '<colgroup><col style="width:60%"><col style="width:12%"><col style="width:28%"></colgroup>',
    '<thead><tr><th>Concepto</th><th style="text-align:center">Cant.</th><th style="text-align:right">Inversión</th></tr></thead>',
    '<tbody>\n' + [filasItems, ...resumen].filter(Boolean).join('\n') + '\n</tbody>',
    '<tfoot><tr><td>' + etiquetaTotal + '</td><td></td>' +
      '<td style="text-align:right">' + esc(fmtMoneda(num(cot.total), moneda)) + '</td></tr></tfoot>',
    '</table>',
  ].join('\n');
}

// ─── {{cronograma_pagos}} ───

function tablaCronograma(cot: DatosBloques): string {
  const cuotas: CuotaCronograma[] = Array.isArray(cot.cronograma_pagos)
    ? (cot.cronograma_pagos as CuotaCronograma[])
    : [];
  if (cuotas.length === 0) return '';

  const moneda = cot.moneda || 'USD';
  const alcance = cot.mostrar_precios === false;

  // Sin importes el calendario de cobro no viene al caso, pero las condiciones
  // sí son alcance: qué libera cada hito se mantiene, el dinero se cae.
  if (alcance) {
    const filas = cuotas
      .map((cuota, i) =>
        '<tr><td>' + esc(cuota.descripcion || 'Hito ' + (cuota.numero ?? i + 1)) + '</td>' +
        '<td>' + esc(cuota.condicion || '') + '</td></tr>')
      .join('\n');
    return [
      '<table class="cot-section-table">',
      '<colgroup><col style="width:32%"><col style="width:68%"></colgroup>',
      '<thead><tr><th>Hito</th><th>Se libera contra</th></tr></thead>',
      '<tbody>\n' + filas + '\n</tbody>',
      '</table>',
    ].join('\n');
  }

  const filas = cuotas
    .map((cuota, i) => {
      const pct = num(cuota.porcentaje);
      return (
        '<tr><td>' + esc(cuota.descripcion || 'Cuota ' + (cuota.numero ?? i + 1)) + '</td>' +
        '<td style="text-align:center">' + (pct > 0 ? esc(fmtPct(pct)) + '%' : '—') + '</td>' +
        '<td style="text-align:right">' + esc(fmtMoneda(num(cuota.monto), moneda)) + '</td>' +
        '<td>' + esc(cuota.condicion || '') + '</td></tr>'
      );
    })
    .join('\n');

  // El pie suma **lo impreso**, no `total`: si el cronograma quedó desfasado del
  // importe de la cotización, la resta tiene que verse en el documento y no
  // quedar tapada por un total que viene de otra columna.
  const sumaPct = r2(cuotas.reduce((s, c) => s + num(c.porcentaje), 0));
  const sumaMonto = r2(cuotas.reduce((s, c) => s + num(c.monto), 0));

  return [
    '<table class="cot-section-table">',
    '<colgroup><col style="width:24%"><col style="width:8%"><col style="width:20%"><col style="width:48%"></colgroup>',
    '<thead><tr><th>Cuota</th><th style="text-align:center">%</th><th style="text-align:right">Monto</th><th>Se libera contra</th></tr></thead>',
    '<tbody>\n' + filas + '\n</tbody>',
    '<tfoot><tr><td>Total</td>' +
      '<td style="text-align:center">' + esc(fmtPct(sumaPct)) + '%</td>' +
      '<td style="text-align:right">' + esc(fmtMoneda(sumaMonto, moneda)) + '</td>' +
      '<td></td></tr></tfoot>',
    '</table>',
  ].join('\n');
}

// ─── Expansión ───

const BLOQUES: Record<string, (cot: DatosBloques) => string> = {
  tabla_inversion: tablaInversion,
  cronograma_pagos: tablaCronograma,
};

/**
 * El marcador, con el <p> que lo envuelve si está solo dentro de uno:
 * `processContent` mete uno cuando el contenido es Markdown suelto, y una
 * `<table>` dentro de un `<p>` la saca el navegador del párrafo y descuadra el
 * espaciado. Por eso se reemplaza el párrafo entero.
 */
function regexMarcador(nombre: string): RegExp {
  return new RegExp('(?:<p[^>]*>\\s*)?\\{\\{\\s*' + nombre + '\\s*\\}\\}(?:\\s*</p>)?', 'gi');
}

/**
 * `{{precio:390}}` — un importe suelto en la prosa.
 *
 * Va con su propio regex y no en `BLOQUES` porque lleva un argumento: no es
 * un bloque con nombre fijo, es una función de un número. Y no se envuelve el
 * `<p>` como los otros, al revés: esto reemplaza texto DENTRO del párrafo.
 */
const REGEX_PRECIO = /\{\{\s*precio\s*:\s*(-?[\d.]+)\s*\}\}/gi;

function expandirPrecios(html: string, cot: DatosBloques): string {
  const factor = Number(cot.markup?.factor);
  const mult = Number.isFinite(factor) && factor > 0 ? factor : 1;
  return html.replace(REGEX_PRECIO, (_todo, raw: string) => {
    const base = num(raw);
    return esc(fmtMoneda(r2(base * mult), cot.moneda || 'USD'));
  });
}

/** ¿El contenido trae algún marcador? */
export function tieneBloques(html: string): boolean {
  if (!html) return false;
  if (REGEX_PRECIO.test(html)) {
    // `test` con /g deja avanzado `lastIndex` y la siguiente llamada
    // empezaría a media cadena: se reinicia o el segundo documento falla.
    REGEX_PRECIO.lastIndex = 0;
    return true;
  }
  return Object.keys(BLOQUES).some(nombre => regexMarcador(nombre).test(html));
}

/**
 * Reemplaza los marcadores por su tabla. Se llama **después** de
 * `processContent`: así el "¿ya trae HTML?" de processContent decide sobre lo
 * que escribió el autor y no sobre el HTML que inyectamos acá.
 */
export function expandirBloques(html: string, cot: DatosBloques | null | undefined): string {
  if (!html) return '';
  if (!cot) return html;
  let out = html;
  for (const [nombre, render] of Object.entries(BLOQUES)) {
    if (!regexMarcador(nombre).test(out)) continue;
    const tabla = render(cot);
    out = out.replace(regexMarcador(nombre), () => tabla);
  }
  // Después de las tablas: un `{{precio:N}}` dentro de una celda generada no
  // existe —las tablas salen de las columnas— y así este paso sólo mira la
  // prosa del autor.
  return expandirPrecios(out, cot);
}
